// src/components/VerticalGauge.js
import React, { useState } from 'react';

const clamp = (v, min = 0, max = 100) => Math.max(min, Math.min(max, v));

export default function VerticalGauge({
  drawdownPct = 0,   // 0..100 (0 = top/green, 100 = bottom/red)
  width = 80,
  height = 240,
  tooltip = '',
}) {
  const [hover, setHover] = useState(false);
  const v = clamp(drawdownPct, 0, 100);

  // inner padding to stay away from rounded corners
  const inset = 8;
  const radius = 12;
  const innerH = Math.max(0, height - inset * 2);

  // marker line geometry (thicker with a subtle dark edge so it's visible on bright green)
  const lineH = 4; // px
  let lineTop = inset + (innerH * v) / 100 - lineH / 2;
  // keep fully inside the inner clipped area
  lineTop = Math.round(clamp(lineTop, inset, inset + innerH - lineH));

  // --- Permanent drawdown pill ---
  const ddText = `-${v.toFixed(1)}%`;            // display as negative drawdown
  const labelCenterY = lineTop + lineH / 2;
  const labelTop = Math.round(
    clamp(labelCenterY, inset + 6, height - inset - 6)
  ); // keep the pill inside the gauge vertically

  // hover tooltip position (anchored to the line, to the right of the gauge)
  const tipTop = lineTop - 12; // vertically centered-ish on the line

  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* gauge body */}
      <div
        style={{
          width,
          height,
          borderRadius: radius,
          background: 'linear-gradient(to bottom, #ff5858, #1ec971)',
          boxShadow:
            'inset 0 0 0 1px rgba(255,255,255,0.06), 0 1px 0 rgba(0,0,0,0.4)',
          position: 'relative',
        }}
      >
        {/* inner clip to respect rounded corners and hide line ends */}
        <div
          style={{
            position: 'absolute',
            top: inset,
            bottom: inset,
            left: inset,
            right: inset,
            borderRadius: radius,
            overflow: 'hidden',
          }}
        >
          {/* marker line (high-contrast gradient to stay visible on bright backgrounds) */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: lineTop - inset, // relative to inner clip
              height: lineH,
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0.35), #ffffff, rgba(0,0,0,0.35))',
              boxShadow: '0 0 2px rgba(0,0,0,0.25), 0 0 2px rgba(255,255,255,0.7)',
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>

      {/* permanent drawdown pill (always visible) */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: labelTop,
          left: width + 8,                 // to the right of the bar
          transform: 'translateY(-50%)',
          background: '#1b1b1b',
          color: '#eaeaea',
          border: '1px solid #353535',
          borderRadius: 999,
          padding: '2px 8px',
          fontSize: 12,
          lineHeight: '16px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',            // won’t block clicks/hover
          boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        }}
      >
        {ddText}
      </div>

      {/* optional hover tooltip (kept from your original) */}
      {hover && tooltip && (
        <div
          style={{
            position: 'absolute',
            top: tipTop,
            left: width + 8,
            background: '#333',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            zIndex: 2, // stays under your modal overlay if it has a higher z-index
          }}
        >
          {tooltip}
        </div>
      )}
    </div>
  );
}
